import React, { useState, useEffect } from 'react';
import {
  CForm,
  CRow,
  CCol,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CButton,
  CSpinner,
  CAlert,
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const StockStatusInputs = ({ 
  onSubmit, 
  onCancel, 
  isLoading = false, 
  submitResponse = null,
  initialData = null,
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    stockStatusName: '',
    stockStatusDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      setFormData({
        stockStatusName: initialData.StockStatusName || '',
        stockStatusDescription: initialData.StockStatusDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            stockStatusName: '',
            stockStatusDescription: ''
          });
        }
        setShowSuccess(false);
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.stockStatusName.trim()) {
      newErrors.stockStatusName = t('stockStatusNameRequired');
    } else if (formData.stockStatusName.trim().length < 3) {
      newErrors.stockStatusName = 'Stock Status Name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }

      const submitData = {
        SessionUserId: String(userId),
        StockStatusName: formData.stockStatusName.trim(),
        StockStatusDescription: formData.stockStatusDescription.trim()
      };

      if (isEdit && initialData?.StockStatusId) {
        submitData.StockStatusId = initialData.StockStatusId;
      }

      console.log(`Submitting ${isEdit ? 'update' : 'create'} stock data:`, submitData);
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        stockStatusName: '',
        stockStatusDescription: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    return isEdit ? t('stockStatusUpdated') : t('stockStatusCreated');
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateStockStatus') : t('createStockStatus');
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" 
        dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse && submitResponse.Result && 
      submitResponse.Result.ErrorCode !== 0 && submitResponse.Result.ErrorDescription && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="stockStatusName">
              {t('stockStatusName')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="stockStatusName"
              name="stockStatusName"
              value={formData.stockStatusName}
              onChange={handleInputChange}
              invalid={!!errors.stockStatusName}
              placeholder={t('enterStockStatusName')}
              disabled={isLoading}
            />
            {errors.stockStatusName && (
              <div className="invalid-feedback d-block">
                {errors.stockStatusName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-4">
          <CCol md={12}>
            <CFormLabel htmlFor="stockStatusDescription">
              {t('stockStatusDescription')}
            </CFormLabel>
            <CFormTextarea
              id="stockStatusDescription"
              name="stockStatusDescription"
              rows={4}
              value={formData.stockStatusDescription}
              onChange={handleInputChange}
              invalid={!!errors.stockStatusDescription}
              placeholder={t('enterStockStatusDescription')}
              disabled={isLoading}
            />
            {errors.stockStatusDescription && (
              <div className="invalid-feedback d-block">
                {errors.stockStatusDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>

            <CButton
              color="secondary"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>

      {/* Debug info - development only */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-3 bg-light rounded">
          <small>
            <strong>Debug Info:</strong><br/>
            Is Edit Mode: {String(isEdit)}<br/>
            Form Data: {JSON.stringify(formData, null, 2)}<br/>
            Initial Data: {JSON.stringify(initialData, null, 2)}<br/>
            Errors: {JSON.stringify(errors, null, 2)}<br/>
            Is Loading: {String(Boolean(isLoading))}<br/>
            Submit Response: {submitResponse ? JSON.stringify(submitResponse, null, 2) : 'None'}
          </small>
        </div>
      )} */}
    </div>
  );
};

StockStatusInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default StockStatusInputs;
